import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ProductMediaThumb from "../Product/ProductMediaPreview";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";
import { formatMoney, priceBreakdown, splitMedia } from "./jewelleryUtils";

const FLOW = ["PENDING", "SUCCESS", "READYTODISPATCH", "DISPATCHED"];

const statusLabel = (status) => {
  switch (status) {
    case "READYTODISPATCH":
      return "Ready to dispatch";
    case "SUCCESS":
      return "Confirmed";
    case "PENDING":
      return "Pending";
    case "DISPATCHED":
      return "Dispatched";
    case "CANCELLED":
      return "Cancelled";
    case "RETURNED":
      return "Returned";
    default:
      return status || "Pending";
  }
};

const statusColor = (status) => {
  switch (status) {
    case "PENDING":
      return "#ed6c02";
    case "SUCCESS":
      return "#2e7d32";
    case "CANCELLED":
    case "RETURNED":
    case "FAILED":
      return "#d32f2f";
    case "DISPATCHED":
    case "READYTODISPATCH":
      return "#6c63ff";
    default:
      return "#6c757d";
  }
};

const Row = ({ label, value }) => {
  if (value == null || value === "") return null;
  return (
    <Stack direction="row" justifyContent="space-between" gap={2} py={0.7}>
      <Typography>{label}</Typography>
      <Typography fontWeight={700} textAlign="right">
        {value}
      </Typography>
    </Stack>
  );
};

export default function JewelleryOrderDetails() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const themeColors = useAppTheme();
  const { symbol } = useCurrency();
  const order = state?.order;

  if (!order) {
    return (
      <Typography sx={{ p: 4, textAlign: "center" }}>
        No artificial jewellery order found
      </Typography>
    );
  }

  const media = splitMedia(order.files);
  const status = order.status || "PENDING";
  const stopped = ["CANCELLED", "RETURNED", "FAILED"].includes(status);
  const stepIndex = Math.max(FLOW.indexOf(status), 0);
  const created = order.createdAt
    ? new Date(Number(order.createdAt)).toLocaleString("en-IN")
    : "";
  const price = priceBreakdown(order, order.quantity || 1);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: themeColors.scaffoldGradientColors,
        p: { xs: 2, md: 4 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 900,
          boxShadow: 5,
          p: { xs: 2, md: 3 },
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            onClick={() => navigate("/artificialJewelleryOrder")}
            sx={{ textTransform: "none", color: themeColors.primary }}
          >
            Back to orders
          </Button>
          <Chip
            label={statusLabel(status)}
            sx={{ fontWeight: 700, color: "#fff", background: statusColor(status) }}
          />
        </Stack>

        <Typography
          variant="overline"
          sx={{ display: "block", textAlign: "center", mt: 1, color: themeColors.primary }}
        >
          Artificial Jewellery
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, textAlign: "center", color: themeColors.primary, my: 1 }}
        >
          {order.orderId || "Artificial jewellery order"}
        </Typography>

        {stopped ? (
          <Typography textAlign="center" sx={{ mb: 2 }}>
            This order is {statusLabel(status).toLowerCase()}.
          </Typography>
        ) : (
          <Stepper activeStep={stepIndex} alternativeLabel sx={{ mb: 3 }}>
            {["Placed", "Confirmed", "Ready to dispatch", "Dispatched"].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <Card
          sx={{
            borderRadius: 3,
            background: themeColors.mediumContainer,
            color: themeColors.text,
          }}
        >
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
              <ProductMediaThumb
                urls={media.images}
                videos={media.videos}
                name={order.name}
                width={140}
                height={140}
              />
              <Box flex={1}>
                <Typography variant="h6" fontWeight={800}>
                  {order.name}
                </Typography>
                <Typography variant="body2">{order.productCode}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Divider sx={{ my: 2 }} />
        <Row label="Quantity" value={order.quantity || 1} />
        <Row label="MRP" value={formatMoney(symbol, price.mrp)} />
        <Row
          label="Discount"
          value={
            price.discountPercent > 0
              ? `− ${formatMoney(symbol, price.discountAmount)} (${price.discountPercent}%)`
              : formatMoney(symbol, 0)
          }
        />
        <Row label="Discounted price" value={formatMoney(symbol, price.discounted)} />
        <Row
          label="Tax"
          value={
            price.gstPercent > 0
              ? `${formatMoney(symbol, price.tax)} (GST ${price.gstPercent}%)`
              : formatMoney(symbol, 0)
          }
        />
        <Row label="Payable amount" value={formatMoney(symbol, Number(order.amount) || price.payable)} />
        <Row label="Placed on" value={created} />
        <Row label="Note" value={order.customernote} />
      </Card>
    </Box>
  );
}
